import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { BoardListItem } from '../utils/board';
import {
  fetchBoardMenu,
  getBoardMenuFromCache,
  invalidateBoardMenu as invalidateBoardMenuCache,
  subscribeBoardMenu,
} from '../utils/boardMenu';

interface BoardMenuContextValue {
  projectCode: string;
  boards: BoardListItem[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  invalidate: () => Promise<void>;
}

const BoardMenuContext = createContext<BoardMenuContextValue | null>(null);

interface BoardMenuProviderProps {
  projectCode: string;
  children: React.ReactNode;
}

export const BoardMenuProvider: React.FC<BoardMenuProviderProps> = ({
  projectCode,
  children,
}) => {
  const [boards, setBoards] = useState<BoardListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (force = false) => {
      if (!projectCode) return;
      setIsLoading(true);
      setError(null);
      try {
        const list = await fetchBoardMenu(projectCode, { force });
        setBoards(list);
      } catch (err) {
        setError(err instanceof Error ? err.message : '게시판 목록을 불러오지 못했습니다.');
        setBoards([]);
      } finally {
        setIsLoading(false);
      }
    },
    [projectCode]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  useEffect(() => {
    return subscribeBoardMenu(() => {
      const cached = getBoardMenuFromCache(projectCode);
      if (cached) setBoards(cached);
    });
  }, [projectCode]);

  const refresh = useCallback(async () => {
    await load(true);
  }, [load]);

  const invalidate = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const list = await invalidateBoardMenuCache(projectCode);
      setBoards(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : '게시판 목록을 불러오지 못했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [projectCode]);

  const value = useMemo(
    () => ({
      projectCode,
      boards,
      isLoading,
      error,
      refresh,
      invalidate,
    }),
    [projectCode, boards, isLoading, error, refresh, invalidate]
  );

  return <BoardMenuContext.Provider value={value}>{children}</BoardMenuContext.Provider>;
};

export function useBoardMenu(): BoardMenuContextValue {
  const ctx = useContext(BoardMenuContext);
  if (!ctx) {
    throw new Error('useBoardMenu must be used within BoardMenuProvider');
  }
  return ctx;
}

export function useBoardMenuOptional(): BoardMenuContextValue | null {
  return useContext(BoardMenuContext);
}

/** Provider 밖(매장/상품 등)에서 캐시 warm-up */
export function useEnsureBoardMenu(projectCode: string | undefined): {
  boards: BoardListItem[];
  isLoading: boolean;
} {
  const ctx = useBoardMenuOptional();
  const [boards, setBoards] = useState<BoardListItem[]>(ctx?.boards ?? []);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (ctx || !projectCode) return;

    let cancelled = false;
    setIsLoading(true);
    fetchBoardMenu(projectCode)
      .then((list) => {
        if (!cancelled) setBoards(list);
      })
      .catch(() => {
        if (!cancelled) setBoards([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return subscribeBoardMenu(() => {
      void fetchBoardMenu(projectCode).then((list) => {
        if (!cancelled) setBoards(list);
      });
    });
  }, [ctx, projectCode]);

  if (ctx) {
    return { boards: ctx.boards, isLoading: ctx.isLoading };
  }

  return { boards, isLoading };
}
